// src/printing/printService.js
import { buildInvoiceHtml, buildPicklistHtml } from "./buildInvoiceHtml";

/** -------- platform detection (Tauri v2) -------- */
async function getPlatformSafe() {
  try {
    const os = await import("@tauri-apps/plugin-os");
    const p = await os.platform(); // windows | macos | linux | android | ios ...
    return String(p || "").toLowerCase();
  } catch {
    return "web";
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/** ✅ Android/iOS/Web: system print dialog */
async function printHtmlSystemDialog(html) {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    throw new Error("لا يمكن إنشاء نافذة طباعة داخلية.");
  }

  doc.open();
  doc.write(html);
  doc.close();

  await sleep(350);

  iframe.contentWindow.focus();
  iframe.contentWindow.print();

  setTimeout(() => iframe.remove(), 1200);
}

/** ✅ Windows only: direct print via plugin */
async function printHtmlWindowsPlugin({
  html,
  printer,
  page_width = 80,
  page_height = 220,
  id = `job-${Date.now()}`
}) {
  const api = await import("tauri-plugin-printer-v2");
  const { printHtml } = api;

  const wkArgs = [
    "--disable-smart-shrinking",
    "--margin-left", "0",
    "--margin-right", "0",
    "--margin-top", "0",
    "--margin-bottom", "0",
    "--dpi", "203"
  ];
  const print_settings = JSON.stringify({ wkhtml_args: wkArgs });

  return printHtml({
    id,
    html,
    printer,
    print_settings,
    margin: { top: 0, right: 0, bottom: 0, left: 0, unit: "mm" },
    page_width,
    page_height,
    orientation: "portrait",
    copies: 1,
    quality: 100,
    grayscale: false,
    remove_after_print: false
  });
}

function getSavedPrinters() {
  return [
    localStorage.getItem("printer_1") || "",
    localStorage.getItem("printer_2") || "",
    localStorage.getItem("printer_3") || "",
    localStorage.getItem("printer_4") || "",
    localStorage.getItem("printer_5") || ""
  ].filter(Boolean);
}

/**
 * ✅ نفس الدالة اللي تستدعيها من InvoicesList و CreateInvoice
 * - Windows: يطبع للطابعة1 (فاتورة) + الطابعة2 (تجهيز)
 * - Android/iOS: يفتح system print dialog (فاتورة فقط افتراضيًا)
 */
export async function printInvoiceToTwoPrinters({
  invoiceNo,
  lines,
  subtotal,
  discount,
  total,
  customerName,
  brand // ✅ اختياري (حتى لا يكسر الاستدعاءات القديمة)
}) {
  const platform = await getPlatformSafe();

  const items = Array.isArray(lines) ? lines : [];

  const pageHeightInvoiceMm = 220;
  const pageHeightPickMm = 200;

  const htmlInvoice = buildInvoiceHtml({
    invoiceNo,
    lines: items,
    subtotal,
    discount,
    total,
    customerName,
    printedAt: new Date(),
    pageHeightMm: pageHeightInvoiceMm,
    contentWidthMm: 78,
    brand
  });

  const htmlPick = buildPicklistHtml({
    invoiceNo,
    lines: items,
    customerName,
    printedAt: new Date(),
    pageHeightMm: pageHeightPickMm,
    contentWidthMm: 78,
    brand
  });

  // ✅ Android/iOS: النظام (يعرض طابعات BT/Wi-Fi عبر خدمات الطباعة)
  if (platform === "android" || platform === "ios") {
    await printHtmlSystemDialog(htmlInvoice);

    // إذا تريد “تجهيز” أيضاً على نفس الطابعة (اختياري):
    // await printHtmlSystemDialog(htmlPick);

    return;
  }

  // ✅ Windows: direct plugin
  if (platform === "windows") {
    const [p1, p2] = getSavedPrinters();
    if (!p1) throw new Error("حدد الطابعة 1 من صفحة إعداد الطابعات.");

    const a = printHtmlWindowsPlugin({
      html: htmlInvoice,
      printer: p1,
      page_width: 80,
      page_height: pageHeightInvoiceMm,
      id: `inv-${Date.now()}`
    });

    const b = p2
      ? printHtmlWindowsPlugin({
          html: htmlPick,
          printer: p2,
          page_width: 80,
          page_height: pageHeightPickMm,
          id: `pick-${Date.now()}`
        })
      : Promise.resolve();

    await Promise.all([a, b]);
    return;
  }

  // ✅ Web fallback
  await printHtmlSystemDialog(htmlInvoice);
}
